import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // 개발환경에서만 콘솔 로깅
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // 에러 상태 업데이트
    this.setState({
      error: error,
      errorInfo: errorInfo,
      eventId: this.generateEventId()
    });
    
    // 에러 로깅 및 리포팅
    this.logError(error, errorInfo);
  }
  
  generateEventId = () => {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };
  
  logError = (error, errorInfo) => {
    const errorReport = {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      errorInfo,
      userAgent: navigator.userAgent,
      url: window.location.href,
      eventId: this.state.eventId,
      retryCount: this.state.retryCount
    };
    
    // localStorage에 에러 로그 저장 (최대 50개)
    try {
      const errorLogs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      errorLogs.unshift(errorReport);
      if (errorLogs.length > 50) errorLogs.splice(50);
      localStorage.setItem('errorLogs', JSON.stringify(errorLogs));
    } catch (e) {
      // localStorage 저장 실패시 무시
    }
    
    // 프로덕션에서 에러 리포팅 서비스로 전송 (예: Sentry)
    if (process.env.NODE_ENV === 'production') {
      // 여기에 실제 에러 리포팅 서비스 호출 코드 추가
      // Sentry.captureException(error, { extra: errorReport });
    }
  };
  
  handleRetry = () => {
    this.setState(prevState => ({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.82 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
              문제가 발생했습니다
            </h1>
            
            <p className="text-gray-600 text-center mb-6">
              예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 잠시 후 다시 시도해주세요.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                다시 시도 ({this.state.retryCount < 3 ? `${3 - this.state.retryCount}회 남음` : '제한 초과'})
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                페이지 새로고침
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                홈으로 돌아가기
              </button>
            </div>
            
            {this.state.eventId && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-sm text-blue-700">
                  오류 ID: <code className="font-mono">{this.state.eventId}</code>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  문제가 계속되면 이 ID를 고객 지원팀에 제공해주세요.
                </p>
              </div>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 p-4 bg-gray-50 rounded-lg">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                  개발자 정보 (개발 환경에서만 표시)
                </summary>
                <div className="text-xs text-gray-600 space-y-2">
                  <div>
                    <strong>Error:</strong> {this.state.error && this.state.error.toString()}
                  </div>
                  <div>
                    <strong>Retry Count:</strong> {this.state.retryCount}
                  </div>
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1 p-2 bg-white rounded text-xs overflow-auto max-h-32">
                      {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                  <div>
                    <strong>Error Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1 p-2 bg-white rounded text-xs overflow-auto max-h-32">
                      {this.state.error && this.state.error.stack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;